import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import classnames from 'classnames';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';

import { ArrowIcon } from '@/assets/icons';
import { ExportIconWhite, FileIcon } from '@/assets/images';
import { PairwiseDiff } from '@/common/types';
import { ContextMenu } from '@/components/ContextMenu';
import { Droppable } from '@/components/Droppable';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useContextMenu } from '@/contexts/overview/dataroom/useContextMenu';
import { useRedlinesContext } from '@/contexts/overview/redlines/utils';
import { usePresignedUrl } from '@/hooks/usePresignedUrl';
import { ScrollableDiv } from '@/pages/overview/common/ScrollableDiv';
import { useOverview } from '@/pages/overview/common/utils';
import {
  DraggableRedlineViewFile,
  FileData,
} from '@/pages/overview/redline/diffing/DraggableRedlineViewFile';
import { downloadPairwiseDiff, generatePairwiseDiffExport } from '@/pages/overview/redline/utils';
import { PdfViewer } from '@/pages/pdf-viewer/PdfViewer';
import { showSuccessToast } from '@/utils/toasts/Toasts';

export const RedlineDiffingSidebar = () => {
  const [displayedFiles, setDisplayedFiles] = useState<
    {
      id: string;
      name: string;
      distance: number;
      pdfUrl: string;
      displayName: string;
    }[]
  >([]);
  const { isContextMenuVisible, setIsContextMenuVisible, position, setPosition } = useContextMenu();
  const [hoveredFile, setHoveredFile] = useState<FileData | null>(null);
  const { redlineName } = useParams();
  const navigate = useNavigate();
  const [signedUrl, setSelectedFileName, selectedFileName, resetFileUrl] = usePresignedUrl();
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
  const [dragFileName, setDragFileName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [pdfDisplayName, setPdfDisplayName] = useState('');
  const [exportingPairs, setExportingPairs] = useState<Record<string, boolean>>({});
  const [downloadType, setDownloadType] = useState<'pdf' | 'docx' | null>('pdf');
  const shownToastsRef = useRef<Set<string>>(new Set());

  const {
    comparingFiles,
    setComparingFiles,
    templateFileId,
    setTemplateFileId,
    focusedFiles,
    focusedOperation,
    allFileDifferences,
    loading,
    selectedRedline,
  } = useRedlinesContext();

  const {
    matter: { dataRoomFiles, id: matterId },
    isUserCurrentEditor,
  } = useOverview();

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 15,
    },
  });
  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor);

  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor, pointerSensor);

  useEffect(() => {
    if (allFileDifferences?.size) {
      // Convert Map to an array of objects with complete file data
      const fileDifferenceArray = Array.from(allFileDifferences.entries())
        .map(([fileId, distance]) => {
          const fileData = dataRoomFiles.find((file) => file.id === fileId);
          // Only include files that have all required data
          if (!fileData?.name || !fileData?.pdfUrl || !fileData?.displayName) {
            return null;
          }
          return {
            id: fileData.id,
            name: fileData.name,
            distance: distance,
            pdfUrl: fileData.pdfUrl,
            displayName: fileData.displayName,
          };
        })
        .filter((file): file is NonNullable<typeof file> => file !== null); // Filter out null entries

      // Sort the array by distance
      fileDifferenceArray.sort((a, b) => b.distance - a.distance);
      // Update the state with the sorted array
      setDisplayedFiles(fileDifferenceArray);
    }
  }, [allFileDifferences, dataRoomFiles]);

  const handlePdfViewerClose = () => {
    setIsPdfViewerOpen(false);
    resetFileUrl();
  };

  const currentPairKey = useMemo(() => {
    if (comparingFiles.length !== 2 || !templateFileId) return '';

    // names (shorter & unique)  ►  baseline | comparing
    const baselineId = dataRoomFiles.find((f) => f.id === templateFileId)?.id ?? templateFileId;
    const otherFile = comparingFiles
      .map((id) => dataRoomFiles.find((f) => f.id === id))
      .find((f) => f && f.id !== templateFileId);
    const otherId = otherFile?.id ?? '';

    return [baselineId, otherId].sort().join('--');
  }, [comparingFiles, templateFileId, dataRoomFiles]);

  const toggleCheckbox = (file: FileData) => {
    if (comparingFiles.some((checkedFile) => checkedFile === file.id)) {
      if (comparingFiles.length === 2 && availableExportUrls.hasExports) {
        setExportingPairs((prev) => ({ ...prev, [currentPairKey]: false }));
      }
      setComparingFiles(comparingFiles.filter((checkedFile) => checkedFile !== file.id));
    } else {
      setComparingFiles([...comparingFiles, file.id]);
    }
  };

  const CheckBox = ({ file }: { index: number; file: FileData }) => {
    return (
      <div className="flex items-center justify-center" key={file.name}>
        <input
          type="checkbox"
          readOnly
          checked={comparingFiles.some((checkedFile) => checkedFile === file.id)}
          className={`size-4 cursor-pointer appearance-none border border-light-border bg-marveri-background bg-contain bg-center bg-no-repeat checked:bg-[url('@/assets/images/check-icon-white.svg')]`}
          data-testid="redline-diff-checkbox"
        />
      </div>
    );
  };

  const handleSidebarFileClick = (redlineFileName: string | undefined) => {
    const file = dataRoomFiles.find((file) => file.displayName === redlineFileName);
    if (file) {
      setSelectedFileName(file.name);
      setPdfDisplayName(file.displayName);
    } else if (redlineFileName) {
      setSelectedFileName(redlineFileName);
      setPdfDisplayName(redlineFileName);
    }
  };

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const file = event.active.data.current?.file as FileData;
    if (file) {
      setDragFileName(file.displayName);
    }
  }, []);

  const swapTemplateWithComparingFile = useCallback(
    (file: FileData) => {
      setTemplateFileId(file.id);

      // Automatically add it to comparingFiles if it wasn't already there
      const newComparingFiles = comparingFiles.includes(file.id)
        ? comparingFiles
        : [...comparingFiles, file.id];
      setComparingFiles(newComparingFiles);
    },
    [setTemplateFileId, setComparingFiles, comparingFiles],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      // Check if we have file data
      const draggedFile = event.active.data.current?.file as FileData;
      if (event.over?.id === 'template-drop-zone' && draggedFile) {
        swapTemplateWithComparingFile(draggedFile);
      }
    },
    [swapTemplateWithComparingFile],
  );

  const setTemplateToHovered = useCallback(
    (hoveredFile: FileData) => {
      swapTemplateWithComparingFile(hoveredFile);
    },
    [swapTemplateWithComparingFile],
  );

  useEffect(() => {
    const findFile = dataRoomFiles.find((file) => file.id === templateFileId);
    if (findFile) {
      setDisplayName(findFile.displayName);
    } else {
      if (templateFileId) {
        setDisplayName(templateFileId);
      }
    }
  }, [dataRoomFiles, templateFileId]);

  const handleClusterButtonClick = () => {
    unstable_batchedUpdates(() => {
      allFileDifferences?.clear();
      setDisplayedFiles([]);
    });
    navigate(`../${redlineName}/clustering`);
  };

  // Check if export URLs are available for direct download
  const availableExportUrls = useMemo(() => {
    if (comparingFiles.length !== 2 || !templateFileId) {
      return { hasExports: false, pdfAvailable: false, docxAvailable: false, pairwiseDiffId: null };
    }

    // Find the baseline and comparing files
    const baselineFile = dataRoomFiles.find((file) => file.id === templateFileId);
    const comparingFileId = comparingFiles.find((id) => id !== templateFileId);
    const comparingFile = comparingFileId
      ? dataRoomFiles.find((file) => file.id === comparingFileId)
      : null;

    if (!baselineFile || !comparingFile) {
      return { hasExports: false, pdfAvailable: false, docxAvailable: false, pairwiseDiffId: null };
    }

    // Find matching pairwise diff - only match in the correct direction
    // The template_file should match our baseline file and comparing_file should match our comparing file
    const pairwiseDiffs = (selectedRedline?.pairwise_diffs || []) as PairwiseDiff[];
    const matchingPairwiseDiff = pairwiseDiffs.find((diff) => {
      const templateFileMatchesBaseline =
        diff.template_file?.file?.id === baselineFile.id ||
        diff.template_file?.file?.name === baselineFile.name ||
        diff.template_file?.file?.pdfUrl === baselineFile.pdfUrl;

      const comparingFileMatchesOther =
        diff.comparing_file?.file?.id === comparingFile.id ||
        diff.comparing_file?.file?.name === comparingFile.name ||
        diff.comparing_file?.file?.pdfUrl === comparingFile.pdfUrl;

      // Only match in the correct direction
      return templateFileMatchesBaseline && comparingFileMatchesOther;
    });

    if (matchingPairwiseDiff) {
      return {
        hasExports: !!(matchingPairwiseDiff.pdf_export_url || matchingPairwiseDiff.docx_export_url),
        pdfAvailable: !!matchingPairwiseDiff.pdf_export_url,
        docxAvailable: !!matchingPairwiseDiff.docx_export_url,
        pairwiseDiffId: matchingPairwiseDiff.id,
      };
    }

    return { hasExports: false, pdfAvailable: false, docxAvailable: false, pairwiseDiffId: null };
  }, [comparingFiles, templateFileId, dataRoomFiles, selectedRedline]);

  //UseEffect to reset export loading state when export URLs are available
  useEffect(() => {
    if (availableExportUrls.hasExports) {
      setExportingPairs((prev) => {
        if (prev[currentPairKey] && !shownToastsRef.current.has(currentPairKey)) {
          showSuccessToast('Redline export is ready for download.', 'w-[225px]');
          shownToastsRef.current.add(currentPairKey);
        }
        return { ...prev, [currentPairKey]: false };
      });
    }
  }, [availableExportUrls.hasExports, currentPairKey]);

  // Determine if exactly 2 documents are selected total (baseline + 1 comparing)
  const canExport = useMemo(() => {
    // We need exactly one document selected for comparison and a valid template
    return (
      comparingFiles.length === 2 && templateFileId !== undefined && !availableExportUrls.hasExports
    );
  }, [comparingFiles, templateFileId, availableExportUrls.hasExports]);

  // Get displayName for baseline document for export
  const baselineDisplayName = useMemo(() => {
    if (!templateFileId) return '';

    const file = dataRoomFiles.find((file) => file.id === templateFileId);
    return file?.displayName || '';
  }, [dataRoomFiles, templateFileId]);

  // Get displayName for comparing document for export
  const comparingDisplayName = useMemo(() => {
    if (comparingFiles.length !== 2) return '';

    // Get the file that's not the template file
    const comparingFileId = comparingFiles.find((id) => id !== templateFileId);
    const file = comparingFileId ? dataRoomFiles.find((file) => file.id === comparingFileId) : null;
    return file?.displayName || '';
  }, [dataRoomFiles, comparingFiles, templateFileId]);

  // Handle direct download when export URLs are available
  const handleDirectDownload = (format: 'pdf' | 'docx') => {
    if (!availableExportUrls.pairwiseDiffId) {
      return;
    }

    // Use the downloadPairwiseDiff function with the display names and specified format
    downloadPairwiseDiff(
      availableExportUrls.pairwiseDiffId,
      baselineDisplayName,
      comparingDisplayName,
      format,
    );
  };

  // Handle export button click
  const handleExport = async () => {
    if (exportingPairs[currentPairKey]) {
      return;
    }
    if (canExport) {
      setExportingPairs((prev) => ({ ...prev, [currentPairKey]: true }));
      // Should have exactly 2 comparing files
      if (comparingFiles.length !== 2) {
        return;
      }

      // Get the two comparing files from dataroom
      const comparingDataroomFiles = comparingFiles
        .map((fileId) => dataRoomFiles.find((file) => file.id === fileId))
        .filter((file): file is NonNullable<typeof file> => file !== null);

      if (comparingDataroomFiles.length !== 2) {
        return;
      }

      // Find the baseline file (the one that matches templateFileId)
      const baselineDataroomFile = comparingDataroomFiles.find(
        (file) => file.id === templateFileId,
      );
      if (!baselineDataroomFile) {
        return;
      }

      // Find the comparing file (the one that doesn't match templateFileId)
      const comparingDataroomFile = comparingDataroomFiles.find(
        (file) => file.id !== templateFileId,
      );
      if (!comparingDataroomFile) {
        return;
      }

      // Now find the corresponding RedlineFile IDs from the selectedRedline
      if (!selectedRedline || !selectedRedline.redlineFiles) {
        alert('Unable to export the comparison. No redline files found.');
        return;
      }

      // Find the redline files that match our dataroom files
      const baselineRedlineFile = selectedRedline.redlineFiles.find(
        (redlineFile) =>
          redlineFile.file?.id === baselineDataroomFile.id ||
          redlineFile.file?.name === baselineDataroomFile.name ||
          redlineFile.file?.pdfUrl === baselineDataroomFile.pdfUrl,
      );

      const comparingRedlineFile = selectedRedline.redlineFiles.find(
        (redlineFile) =>
          redlineFile.file?.id === comparingDataroomFile.id ||
          redlineFile.file?.name === comparingDataroomFile.name ||
          redlineFile.file?.pdfUrl === comparingDataroomFile.pdfUrl,
      );

      if (!baselineRedlineFile || !comparingRedlineFile) {
        alert('Unable to export the comparison. Could not find matching redline files.');
        return;
      }

      // Call the API to generate the export using the pairwise diff approach with RedlineFile IDs
      try {
        await generatePairwiseDiffExport(baselineRedlineFile.id, comparingRedlineFile.id, matterId);
      } catch (error) {
        alert(
          'Unable to export the comparison. This might be because the files need to be regenerated. Try recreating the redline with these files.',
        );
        setExportingPairs((prev) => ({ ...prev, [currentPairKey]: false }));
      }
    }
  };

  return (
    <>
      <div className="flex h-[80vh] flex-col justify-between">
        <div className="flex flex-col  justify-between gap-4 border-b-2 border-light-border py-4 pl-4 text-marveri-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-medium">Redline</span>
              {allFileDifferences && allFileDifferences?.size > 0 && loading && <LoadingSpinner />}
            </div>
            <div className="flex flex-col gap-2">
              {availableExportUrls.hasExports ? (
                <div className="flex items-center gap-2">
                  <Menu as="div" className="relative inline-block text-left">
                    <MenuButton className="flex h-[31px] cursor-pointer items-center justify-center gap-2 rounded-[4px] bg-N600 p-3 text-[12px] font-medium text-[#EAE6EA] hover:opacity-80">
                      <span>{downloadType?.toUpperCase()}</span>
                      <img src={ArrowIcon} className="size-4" />
                    </MenuButton>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems
                        className={`absolute z-10 w-[150px] origin-top-right flex-col rounded-md border border-[#353336] bg-[#171617] text-xs font-normal text-marveri-white shadow-lg drop-shadow-[4px_4px_2px_rgba(0,0,0,0.05)] focus:outline-none`}
                      >
                        <MenuItem
                          as="div"
                          onClick={() => setDownloadType('pdf')}
                          className="cursor-pointer p-3 text-[12px] font-medium text-[#EAE6EA] hover:opacity-80"
                        >
                          PDF
                        </MenuItem>
                        <MenuItem
                          as="div"
                          onClick={() => setDownloadType('docx')}
                          className="cursor-pointer p-3 text-[12px] font-medium text-[#EAE6EA] hover:opacity-80"
                        >
                          DOCX
                        </MenuItem>
                      </MenuItems>
                    </Transition>
                  </Menu>
                  <div
                    onClick={() => handleDirectDownload(downloadType as 'pdf' | 'docx')}
                    data-testid="redline-download-pdf-button"
                    className="flex h-[31px] cursor-pointer items-center justify-center gap-2 rounded-[4px] bg-N600 p-3 text-[12px] font-medium text-[#EAE6EA] hover:opacity-80"
                  >
                    Download file
                  </div>
                </div>
              ) : (
                <div>
                  {exportingPairs[currentPairKey] ? (
                    <div className="flex h-[31px] cursor-default items-center justify-center gap-2 rounded-[4px] bg-N600 p-3 text-[12px] font-medium opacity-80">
                      <LoadingSpinner size={16} />
                      <span>Preparing the export</span>
                    </div>
                  ) : (
                    <div
                      className={`${canExport ? 'cursor-pointer bg-N600 text-[#EAE6EA] hover:opacity-80' : 'cursor-default text-N400'} group relative flex h-[31px] items-center justify-center  gap-2 rounded-[4px] p-3 text-[12px] font-medium`}
                      onClick={handleExport}
                    >
                      <img
                        src={ExportIconWhite}
                        className={`${canExport ? 'filter-hover-filter' : 'opacity-50'}`}
                      />
                      <span>Export</span>
                      {(comparingFiles.length !== 2 ||
                        (comparingFiles.length === 2 && !canExport)) && (
                        <div className="absolute right-0 top-[30px] z-10 hidden w-[180px] rounded-[8px] bg-[#D9D9D9] px-[6px] py-1 text-center text-xs text-black before:absolute before:-top-4 before:right-[10px] before:border-8 before:border-transparent before:border-b-[#D9D9D9] group-hover:block">
                          {comparingFiles.length !== 2
                            ? 'Only comparisons of two documents may be exported.'
                            : 'Redline comparison is still being generated.'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart} sensors={sensors}>
          <Droppable droppableId="template-drop-zone" data={{ current: templateFileId }}>
            <div
              className="border-b-2 border-light-border p-4 text-marveri-white"
              data-testid="baseline-container"
            >
              <h1
                className="pb-2 text-[12px] font-bold text-marveri-light-silver"
                data-testid="baseline-title"
              >
                BASELINE DOCUMENT
              </h1>
              <div
                className="h-[100px] overflow-y-auto scrollbar-thin scrollbar-thumb-marveri-muted-silver"
                data-testid="baseline-content"
              >
                <div
                  className="flex cursor-pointer flex-row items-center py-[10px]"
                  onClick={() => handleSidebarFileClick(displayName)}
                  onDoubleClick={() => setIsPdfViewerOpen(true)}
                  data-testid="baseline-display-name"
                >
                  <img src={FileIcon} className="mx-[10px] my-[3px] size-[16px]" />
                  <span className="select-none text-[12px]">{displayName}</span>
                </div>
              </div>
            </div>
          </Droppable>
          <div className="border-b-2 border-light-border p-4 text-marveri-white">
            <div className="flex justify-between pr-[5px] text-marveri-muted-silver">
              <h1 className="w-full pb-2 text-[12px] font-bold text-marveri-light-silver">
                DOCUMENTS COMPARED
              </h1>
              <h2 className="pb-2 text-[12px] font-bold text-marveri-light-silver">SIMILARITIES</h2>
            </div>
            {allFileDifferences?.size === 0 ? (
              <div className="flex h-[calc(100vh-415px)] translate-y-10 justify-center">
                <LoadingSpinner size={20} />
              </div>
            ) : (
              <ScrollableDiv containerStyle="h-[calc(100vh-415px)]">
                {displayedFiles
                  .filter((file) => {
                    const dataRoomFile = dataRoomFiles.find((f) => f.pdfUrl === file.pdfUrl);
                    return dataRoomFile?.id !== templateFileId;
                  })
                  .map((file, index) => (
                    <DraggableRedlineViewFile
                      key={index}
                      draggableId={index.toString()}
                      file={{
                        id: file.id,
                        name: file.name,
                        pdfUrl: file.pdfUrl,
                        displayName: file.displayName,
                      }}
                      disabled={loading}
                    >
                      <div
                        className={classnames(
                          `relative flex min-h-[60px] cursor-pointer items-center justify-between gap-[14px] border-b border-light-border px-[10px] last:border-b-0 hover:bg-container-hover`,
                          focusedFiles.includes(file.id)
                            ? {
                                'bg-[#8A3333]': focusedOperation === 'delete',
                                'bg-[#3E3E92]': focusedOperation === 'insert',
                              }
                            : {},
                        )}
                        onClick={() =>
                          toggleCheckbox({
                            id: file.id,
                            name: file.name,
                            pdfUrl: file.pdfUrl,
                            displayName: file.displayName,
                          })
                        }
                        onContextMenu={(e: React.MouseEvent) => {
                          // Use the file data we already have
                          setHoveredFile({
                            id: file.id,
                            name: file.name,
                            pdfUrl: file.pdfUrl,
                            displayName: file.displayName,
                          });
                          setIsContextMenuVisible(true);
                          setPosition({
                            x: e.clientX,
                            y: e.clientY,
                          });
                          e.preventDefault();
                        }}
                      >
                        <CheckBox
                          file={{
                            id: file.id,
                            name: file.name,
                            pdfUrl: file.pdfUrl,
                            displayName: file.displayName,
                          }}
                          index={index}
                        />

                        <div
                          className="ml-1 w-full py-[10px] text-[12px]"
                          onClick={(e) => {
                            handleSidebarFileClick(file.name);
                            e.stopPropagation();
                          }}
                          onDoubleClick={(event) => {
                            event.preventDefault();
                            setIsPdfViewerOpen(true);
                          }}
                          data-testid="redline-diff-file"
                        >
                          <div>
                            <div className="flex items-center gap-[12px] text-left">
                              <img src={FileIcon} alt="file-icon" className="h-[12px]" />
                              <span>{file.displayName}</span>
                            </div>
                          </div>
                        </div>
                        <div className="ml-1 py-[10px] text-[12px]">
                          {`${(file.distance * 100).toFixed(0)}%`}
                        </div>
                      </div>
                    </DraggableRedlineViewFile>
                  ))}
                <DragOverlay>
                  <div className="flex cursor-grabbing items-center gap-[8px] rounded border-2 border-light-border bg-marveri-background p-4 drop-shadow-lg">
                    {dragFileName}
                  </div>
                </DragOverlay>
              </ScrollableDiv>
            )}
          </div>
          <div
            className="m-auto mt-2 h-[32px] w-[95px] cursor-pointer rounded border py-[3px] text-center text-[white] hover:bg-container-hover"
            onClick={handleClusterButtonClick}
            data-testid="diff-cluster-button"
          >
            Clusters
          </div>
        </DndContext>
      </div>
      {isContextMenuVisible && (
        <ContextMenu
          disabled={!isUserCurrentEditor}
          options={[
            ...(hoveredFile
              ? [
                  {
                    title: 'Make Baseline Document',
                    onClick: () => setTemplateToHovered(hoveredFile),
                    icon: FileIcon,
                  },
                ]
              : []),
          ]}
          x={position.x}
          y={position.y}
        />
      )}
      {signedUrl && isPdfViewerOpen && selectedFileName && (
        <PdfViewer
          title={pdfDisplayName}
          fileUrl={signedUrl}
          multipleDocuments={false}
          closePdfViewer={handlePdfViewerClose}
        />
      )}
    </>
  );
};
